import { getIsProAlgoliaFilter, type LanguageCode, type ProductIndex } from './product-indices';

type IndexPrefixType = 'dev_' | '';

/**
 * Maps search index names to their document types
 */
export const SEARCH_PAGE_INDEX_TO_DOCTYPE = {
   topic: 'topic',
   product: 'product',
   guide: 'guide',
   post: 'post',
   wiki: 'wiki',
   searchpage: 'pages',
   wp_searchable_posts: 'news',
   document: 'document',
   item: 'item',
} as const;

/**
 * Extract search index names from the definitions
 */
export const SEARCH_PAGE_INDICES = Object.keys(SEARCH_PAGE_INDEX_TO_DOCTYPE) as Array<
   keyof typeof SEARCH_PAGE_INDEX_TO_DOCTYPE
>;

/**
 * Local document types based on our search index definitions
 */
export type Doctype =
   (typeof SEARCH_PAGE_INDEX_TO_DOCTYPE)[keyof typeof SEARCH_PAGE_INDEX_TO_DOCTYPE];
export type SearchPageIndex = keyof typeof SEARCH_PAGE_INDEX_TO_DOCTYPE;

type LangDocTypes = Exclude<
   SearchPageIndex,
   'product' | 'wp_searchable_posts' | 'searchpage' | 'document'
>;
type ENOnlyIndices = `${IndexPrefixType}searchpage_en` | `${IndexPrefixType}document_en`;

export type SearchPageIndices =
   | `${IndexPrefixType}${LangDocTypes}_${LanguageCode}`
   | `${IndexPrefixType}${ProductIndex}`
   | `${IndexPrefixType}wp_searchable_posts`
   | ENOnlyIndices;

type ProdSearchPageIndices = Exclude<SearchPageIndices, `${'dev_'}${string}`>;
type DevSearchPageIndices = Extract<SearchPageIndices, `${'dev_'}${string}`>;

export const orderedDoctypes: Doctype[] = [
   'product',
   'guide',
   'post',
   'wiki',
   'topic',
   'document',
   'news',
];

/**
 * Gets searchable attributes for different doctypes
 * Similar to the configuration in doctypes.js
 */
export function getSearchableAttributes(doctype: Doctype): string[] | undefined {
   const searchableAttributes: Partial<Record<Doctype, string[]>> = {
      topic: ['all_titles', 'variants', 'expanded_years', 'wiki_info'],
      // Add other doctypes' searchable attributes as needed
   };

   return searchableAttributes[doctype];
}

export function createSearchParameters(doctype: Doctype): Record<string, unknown> {
   const searchableAttributes = getSearchableAttributes(doctype);
   const parameters: Record<string, unknown> = {};

   if (searchableAttributes?.length) {
      parameters.restrictSearchableAttributes = searchableAttributes;
   }

   // For distinct filtering (similar to doctypes.js)
   if (doctype === 'post' || doctype === 'news' || doctype === 'product') {
      parameters.distinct = true;
   }

   return parameters;
}

export const allDoctypes: Doctype[] = Object.values(SEARCH_PAGE_INDEX_TO_DOCTYPE);

function isDevPrefixed(indexName: SearchPageIndices): indexName is DevSearchPageIndices {
   return indexName.startsWith('dev_');
}

export function normalizeIndexName(indexName: SearchPageIndices): ProdSearchPageIndices | null {
   if (isDevPrefixed(indexName)) {
      return indexName.replace(/^dev_/, '') as ProdSearchPageIndices;
   }
   return indexName as ProdSearchPageIndices;
}

type FiltersForDocTypeParams = {
   doctype: Doctype;
   isProUser: boolean;
   langid: string;
};

export function getFiltersForDoctype({
   doctype,
   isProUser,
   langid,
}: FiltersForDocTypeParams): string | undefined {
   const filtersByDoctype: Record<Doctype, string[]> = {
      product: [getIsProAlgoliaFilter({ includeProProducts: isProUser })],
      news: [
         langid === 'en' ? 'lang:en<score=1>' : `(lang:en<score=1> OR lang:${langid}<score=2>)`,
      ],
      guide: [],
      post: [],
      wiki: [],
      topic: [],
      document: [],
      pages: [],
      item: [],
   };

   return combineFilters(...(filtersByDoctype[doctype] || []));
}

function combineFilters(...filters: Array<string>): string | undefined {
   const filtered = filters.filter(Boolean);
   return filtered.length > 0 ? filtered.join(' AND ') : undefined;
}
